import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { VENDOR_PAYMENTS, VENDOR_CATEGORIES, GALLERY_IMAGES } from "../lib/data";
import { CreditCard, ArrowUpRight, Check } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const Vendors = () => {
  const [form, setForm] = useState({
    name: "",
    business: "",
    email: "",
    instagram: "",
    category: "",
    website: "",
    market: "",
    description: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    // Placeholder submission — Wix-friendly. The real submission will hook into a Wix Form.
    toast.success("Application received — we'll be in touch within 5 business days.");
    setSubmitted(true);
  };

  return (
    <div data-testid="vendors-page">
      <section className="px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-16">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="tracking-overline text-xs text-[#9A7456] mb-6">
              Vendor Application
            </p>
            <h1
              className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98]"
              data-testid="vendors-title"
            >
              Sell with us — beautifully.
            </h1>
            <p className="font-body text-lg text-[#231F1E]/75 mt-8 max-w-2xl leading-relaxed">
              We design markets the way our vendors deserve them: hand-curated,
              well-laid-out, well-promoted and built around real community.
              If you're a small business, maker, creative or local brand, we'd
              love to host you.
            </p>
          </div>
          <div className="lg:col-span-5 aspect-[4/5] lg:aspect-[3/4] media-zoom">
            <img
              src={GALLERY_IMAGES[14]?.src}
              alt="Vendors at Workin Chix market"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* Who it's for */}
      <section className="px-6 md:px-12 lg:px-16 py-20 border-t border-[#231F1E]/10">
        <p className="tracking-overline text-xs text-[#9A7456] mb-8">
          Who we welcome
        </p>
        <div className="flex flex-wrap gap-3">
          {VENDOR_CATEGORIES.map((c) => (
            <span
              key={c}
              className="px-5 py-2.5 rounded-full border border-[#231F1E]/25 text-sm font-body text-[#231F1E] hover:bg-[#231F1E] hover:text-[#EDE6DA] transition-colors"
              data-testid={`vendor-category-${c.toLowerCase().replace(/\s|\+/g, '-')}`}
            >
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section
        className="bg-[#231F1E] text-[#EDE6DA] px-6 md:px-12 lg:px-16 py-24 md:py-32"
        data-testid="vendor-form-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="tracking-overline text-xs text-[#D6B66B] mb-6">
              The Application
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Tell us about you and your work.
            </h2>
            <p className="font-body text-sm text-[#EDE6DA]/70 mt-6 max-w-md leading-relaxed">
              A short form — we read every submission and respond within 5
              business days. (When this site moves to Wix, this form connects to
              your Wix Form / inbox.)
            </p>
            <p className="font-body text-xs text-[#EDE6DA]/40 mt-8">
              * Required
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="vendor-application-form"
          >
            <Field label="Full name *" name="name" value={form.name} onChange={onChange} required testid="vendor-input-name" />
            <Field label="Business name *" name="business" value={form.business} onChange={onChange} required testid="vendor-input-business" />
            <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required testid="vendor-input-email" />
            <Field label="Instagram handle" name="instagram" value={form.instagram} onChange={onChange} placeholder="@yourbusiness" testid="vendor-input-instagram" />
            <div className="md:col-span-1">
              <label className="block tracking-overline text-[10px] text-[#EDE6DA]/60 mb-2">
                Category *
              </label>
              <select
                name="category"
                required
                value={form.category}
                onChange={onChange}
                data-testid="vendor-input-category"
                className="w-full bg-transparent border-b border-[#EDE6DA]/30 py-3 text-[#EDE6DA] font-body focus:border-[#D6B66B] focus:outline-none transition-colors"
              >
                <option value="" className="bg-[#231F1E]">Select…</option>
                {VENDOR_CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#231F1E]">{c}</option>
                ))}
              </select>
            </div>
            <Field label="Website (optional)" name="website" value={form.website} onChange={onChange} placeholder="https://" testid="vendor-input-website" />
            <Field label="Which market are you applying for? *" name="market" value={form.market} onChange={onChange} required placeholder="e.g. Spring Bloom Market" testid="vendor-input-market" className="md:col-span-2" />
            <div className="md:col-span-2">
              <label className="block tracking-overline text-[10px] text-[#EDE6DA]/60 mb-2">
                Tell us about your work *
              </label>
              <textarea
                name="description"
                required
                rows={5}
                value={form.description}
                onChange={onChange}
                data-testid="vendor-input-description"
                className="w-full bg-transparent border-b border-[#EDE6DA]/30 py-3 text-[#EDE6DA] font-body focus:border-[#D6B66B] focus:outline-none transition-colors resize-none"
              />
            </div>
            <div className="md:col-span-2 mt-4">
              <button
                type="submit"
                disabled={submitted}
                data-testid="vendor-submit-button"
                className="btn-primary !bg-[#D6B66B] !border-[#D6B66B] !text-[#231F1E] hover:!bg-[#EDE6DA] hover:!border-[#EDE6DA] disabled:opacity-60"
              >
                {submitted ? (
                  <>
                    <Check size={16} /> Submitted
                  </>
                ) : (
                  <>
                    Submit Application <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Vendor payments — vendor-side only */}
      <section
        className="px-6 md:px-12 lg:px-16 py-24 md:py-32"
        data-testid="vendor-payments-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-5">
            <p className="tracking-overline text-xs text-[#9A7456] mb-6">
              Vendor Payments
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Pay your vendor fee.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="font-body text-base text-[#231F1E]/75 leading-relaxed max-w-2xl">
              Once your application is accepted, you'll receive an invoice and
              confirmation. Vendor fees are payable via the links below. Please
              include your business name in the payment note.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#231F1E]/15">
          {VENDOR_PAYMENTS.map((p) => (
            <a
              key={p.label}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#EDE6DA] p-10 group hover:bg-[#E5DEC9] transition-colors block"
              data-testid={`payment-${p.label.toLowerCase().replace(' ', '-')}`}
            >
              <CreditCard size={22} className="text-[#9A7456]" />
              <h3 className="font-display text-3xl mt-6">{p.label}</h3>
              <p className="font-body text-sm text-[#231F1E]/65 mt-3">{p.note}</p>
              <p className="font-body text-sm text-[#231F1E] mt-6 tracking-wide">
                {p.handle}
              </p>
              <span className="inline-flex items-center gap-2 mt-8 tracking-overline text-[11px] text-[#231F1E] link-underline">
                Open {p.label} <ArrowUpRight size={14} />
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, placeholder, testid, className = "" }) => (
  <div className={className}>
    <label className="block tracking-overline text-[10px] text-[#EDE6DA]/60 mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-transparent border-b border-[#EDE6DA]/30 py-3 text-[#EDE6DA] font-body placeholder:text-[#EDE6DA]/30 focus:border-[#D6B66B] focus:outline-none transition-colors"
    />
  </div>
);

export default Vendors;
